import { Box, Container, useTheme } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { t } from "@lingui/macro";
import SaveDiscardButtons from "../../Results/SaveDiscardButtons";
import { ErrorBoundary, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";
import Loader from "../../Results/Loader";
import useThreeDPQuizResults from "./useThreeDPQuizResults";
import ResultsHeaderBar from "../../Results/ResultsHeaderBar";
import ResultsHeader from "../../Results/ResultsHeader";
import { ThreeDPQuizResultsType, WahooWorkoutTypeFamily } from "@WahooFitness/cloud-client-types";
import SnailSection from "@/components/FitnessProfile/Shared/SnailSection";
import StrengthWeaknessSection from "@/components/FitnessProfile/Shared/StrengthWeaknessSection";
import { useInView } from "react-intersection-observer";

interface Props {
  results?: ThreeDPQuizResultsType;
  error?: string;
  setPage: Dispatch<SetStateAction<number>>;
  closeOnBack: boolean;
}

function ThreeDPQuizResults({ results, error, setPage, closeOnBack }: Props) {
  const {
    resultsLoading,
    runnerTypeData,
    runnerStrength,
    runnerWeakness,
    saveFitnessProfile,
    discardResults,
    reloadHandler,
    dimensions,
    legendItems,
    analyticsContext,
    openInfoDrawer,
  } = useThreeDPQuizResults(setPage, results, closeOnBack);
  const { palette } = useTheme();

  const { ref: headerBottom, entry } = useInView({
    initialInView: true,
    trackVisibility: true,
    delay: 200,
  });
  return (
    <Box
      // Self-scrolling: the sticky save/discard bar provides its own bottom
      // clearance, so the shell's page-end spacer would double-lift it.
      className="wf-self-scrolling-page"
      bgcolor={palette.background.level1}
      minHeight="100dvh"
    >
      <GlobalHeader hasOwnHeader />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary
          isLoading={resultsLoading || !results}
          error={!!error}
          loadingComponent={<Loader message={t`Calculating your results...`} />}
          onReload={reloadHandler}
        >
          <Box display="flex" flexDirection="column" alignItems="center" height="100%">
            <Box
              sx={{
                position: "relative",
                width: "100vw",
                backgroundColor: palette.background.level1,
              }}
              display="flex"
              pt={2}
              alignItems="center"
              flexDirection="column"
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                maxWidth="sm"
                width="100%"
                gap={1.5}
                pb={3}
              >
                <ResultsHeaderBar
                  athleteType={runnerTypeData?.name}
                  showHeaderBar={entry && !entry?.isIntersecting}
                  athleteTypeIcon={runnerTypeData?.icon}
                />
                <ResultsHeader
                  icon={runnerTypeData?.iconFilled}
                  name={runnerTypeData?.name}
                  profileType={WahooWorkoutTypeFamily.Running}
                />
                <Box ref={headerBottom} />
                <SnailSection
                  workoutFamily={WahooWorkoutTypeFamily.Running}
                  hasProfile={!!results}
                  dimensions={dimensions}
                  legendItems={legendItems}
                  openInfoDrawer={openInfoDrawer}
                  isProfileEstimate={true}
                  isProfileManual={false}
                />
                <StrengthWeaknessSection
                  strength={runnerStrength}
                  weakness={runnerWeakness}
                  analyticsContext={analyticsContext}
                  disabled
                  defaultExpanded
                />
              </Box>
              <SaveDiscardButtons onSave={saveFitnessProfile} onDiscard={discardResults} />
            </Box>
          </Box>
        </ErrorBoundary>
      </Container>
    </Box>
  );
}

export default ThreeDPQuizResults;
